import { FORM_TOKEN_MAX_AGE_MS, MIN_FILL_MS } from './spamGuard.constants';

// Waiting a little past MIN_FILL_MS absorbs clock drift between server instances.
const MIN_AGE_MARGIN_MS = 500;
// Refresh well before the server would call the token expired.
const REFRESH_AFTER_MS = FORM_TOKEN_MAX_AGE_MS - 60 * 60 * 1000;

type CachedToken = { token: string; fetchedAt: number };

let cached: CachedToken | null = null;
let inFlight: Promise<CachedToken> | null = null;

const fetchFormToken = (): Promise<CachedToken> => {
  if (!inFlight) {
    inFlight = fetch('/api/form-token', { cache: 'no-store' })
      .then(async (response) => {
        if (!response.ok) {
          throw new Error(`form token request failed (${response.status})`);
        }
        const { token } = await response.json();
        cached = { token, fetchedAt: Date.now() };
        return cached;
      })
      .finally(() => {
        inFlight = null;
      });
  }
  return inFlight;
};

const isFresh = (entry: CachedToken | null): entry is CachedToken =>
  !!entry && Date.now() - entry.fetchedAt < REFRESH_AFTER_MS;

/** Fetch a token in the background (on form mount) so submitting doesn't have to wait. */
export const prefetchFormToken = (): void => {
  if (!isFresh(cached)) {
    // A failed prefetch is retried by getFormToken at submit time.
    fetchFormToken().catch(() => undefined);
  }
};

/**
 * Resolve a token the server will accept. Only elapsed time on this device is
 * measured, never its absolute clock, so a wrong device clock can't get a real
 * user rejected. If the token is too young (fast autofill, or the prefetch
 * failed) this waits until it's old enough instead of letting the server drop it.
 */
export const getFormToken = async (): Promise<string> => {
  const entry = isFresh(cached) ? cached : await fetchFormToken();
  const wait = MIN_FILL_MS + MIN_AGE_MARGIN_MS - (Date.now() - entry.fetchedAt);
  if (wait > 0) {
    await new Promise((resolve) => setTimeout(resolve, wait));
  }
  return entry.token;
};
